import Image from 'next/image'
import { sanitizeHTML } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'

interface ProcedureAtGlanceData {
	anchor_title?: string
	heading?: {
		title?: string
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					text?: string
					svg_list_icons?: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'anchor_title',
	'heading.title',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'content_collection.site_content_collections_id.collection.item:svg_icons.svg_list_icons',
] as const

interface ProcedureAtGlanceProps {
	data?: ProcedureAtGlanceData
}

/**
 * Helper function to get icon path from svg_list_icons field
 * @param iconName - Icon name from Directus (e.g., "icons-time-light")
 * @returns Full path to the icon (e.g., "/images/icons-time-light.svg")
 */
const getIconPath = (iconName?: string): string => {
	if (!iconName) return '/images/icons-time-light.svg'
	return `/images/${iconName}.svg`
}

export default function ProcedureAtGlance({ data }: ProcedureAtGlanceProps) {
	const sectionRef = useRef<HTMLElement>(null)
	const [isVisible, setIsVisible] = useState(false)
	const sectionIdBase = data?.anchor_title || 'ProcedureAtGlance'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	// Extract cards from content_collection
	const cards = (data?.content_collection || []).map((card) => {
		const titleItem = card.site_content_collections_id?.collection?.find(
			(c) => c.item.title
		)
		const textItem = card.site_content_collections_id?.collection?.find(
			(c) => c.item.text
		)
		const iconItem = card.site_content_collections_id?.collection?.find(
			(c) => c.item.svg_list_icons
		)

		return {
			id: card.id,
			title: titleItem?.item.title || '',
			text: textItem?.item.text || '',
			icon: iconItem?.item.svg_list_icons || '',
		}
	})

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsVisible(entry.isIntersecting)
			},
			{ threshold: 0.15, rootMargin: '-50px' }
		)

		const currentSection = sectionRef.current

		if (currentSection) {
			observer.observe(currentSection)
		}

		return () => {
			if (currentSection) {
				observer.unobserve(currentSection)
			}
		}
	}, [])

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'ProcedureAtGlance'} ref={sectionRef} className='ProcedureAtGlance pb-16 md:py-16 max-md:overflow-hidden'>
			<div className='container'>
				<div className='flex flex-col lg:flex-row justify-between items-start md:gap-10 lg:gap-20'>
					{/* Title */}
					<div
						className={`w-full lg:w-[340px] transition-all duration-700 ease-out ${
							isVisible
								? 'opacity-100 translate-x-0'
								: 'opacity-0 -translate-x-8'
						}`}
					>
						<h2
							className='text-center lg:text-left'
							dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || '')}
						/>
					</div>

					{/* Cards Grid */}
					<div className='flex-1 w-full lg:w-auto'>
						{cards.length > 0 && (
							<div className='grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5'>
								{cards.map((card, index) => (
									<div
										key={card.id}
										className={`group flex flex-col items-center gap-4 p-3 md:p-5 border border-[#030303] rounded-md min-h-[200px] md:min-h-[225px] text-center hover:shadow-lg ${
											isVisible
												? 'opacity-100 translate-y-0'
												: 'opacity-0 translate-y-12'
										}`}
										style={{
											transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
											transitionDelay: isVisible ? `${index * 80}ms` : '0ms',
										}}
									>
										<Image
											src={getIconPath(card.icon)}
											alt={`${card.title} Icon`}
											width={58}
											height={58}
											className='transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-translate-y-1'
										/>
										<h3 className='text-2xl 3xl:text-3xl'>{card.title}</h3>
										<div
											className='prose text-lg 3xl:text-xl'
											dangerouslySetInnerHTML={sanitizeHTML(card.text)}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}

